(() => {
  if (window.SPCLSTFW26Homepage) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pad2 = (value) => String(value).padStart(2, '0');
  let youtubeApiPromise;

  const loadYouTubeApi = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeApiPromise) return youtubeApiPromise;

    youtubeApiPromise = new Promise((resolve) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousReady === 'function') previousReady();
        resolve(window.YT);
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      }
    });

    return youtubeApiPromise;
  };

  const playSafely = (video) => {
    if (!video || reducedMotion.matches) return;
    const result = video.play();
    if (result?.catch) result.catch(() => {});
  };

  const initHero = (root) => {
    if (!root || root.dataset.fw26Ready === 'true') return;
    root.dataset.fw26Ready = 'true';

    const videos = [...root.querySelectorAll('[data-fw26-hero-right-video]')];
    const mobileImage = root.querySelector('[data-fw26-hero-mobile-image]');
    const mobileHero = window.matchMedia('(max-width: 991px)');
    const count = root.querySelector('[data-fw26-hero-count]');
    const progress = [...root.querySelectorAll('.spclst-fw26-hero__progress i')];
    const playButton = root.querySelector('[data-fw26-hero-play]');
    let activeIndex = 0;
    let activeVideoIndex = 0;
    let playAttempt = 0;
    let playTimeout;
    let watchedVideo;
    let lastProgressTime = 0;
    let retryPosition = 0;
    let userPaused = false;
    let recoveryControl = false;
    let disposed = false;
    let heroVisible = root.getBoundingClientRect().bottom > 0 && root.getBoundingClientRect().top < window.innerHeight;

    const canRun = () => !disposed && !reducedMotion.matches && document.visibilityState !== 'hidden' && heroVisible && !userPaused;
    const updateControl = (state) => {
      root.dataset.fw26Playback = state;
      if (!playButton) return;
      const video = videos[activeVideoIndex];
      playButton.hidden = reducedMotion.matches || !heroVisible || document.visibilityState === 'hidden' || !(recoveryControl || userPaused);
      const playing = video && state === 'playing' && !video.paused;
      const stalled = video && state === 'stalled';
      playButton.textContent = stalled ? 'Retry video' : playing ? 'Pause video' : 'Play video';
      playButton.setAttribute('aria-label', stalled ? 'Retry loading hero video' : playing ? 'Pause hero playback' : 'Play hero playback');
      playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
    };
    const pauseMedia = () => {
      playAttempt += 1;
      window.clearTimeout(playTimeout);
      playTimeout = undefined;
      watchedVideo = undefined;
      videos.forEach((video) => video.pause());
    };
    const watchProgress = (video) => {
      window.clearTimeout(playTimeout);
      watchedVideo = video;
      lastProgressTime = video.currentTime;
      playTimeout = window.setTimeout(() => {
        playTimeout = undefined;
        if (video !== videos[activeVideoIndex] || !canRun() || video.ended) return;
        recoveryControl = true;
        root.dataset.fw26PlaybackError = 'PlaybackStalled';
        updateControl('stalled');
      }, 8000);
    };
    const startVideo = (video) => {
      if (!video || !canRun()) return;
      const deferredSource = video.querySelector('source[data-fw26-src]');
      if (video.dataset.fw26Poster && !video.hasAttribute('poster')) {
        video.setAttribute('poster', video.dataset.fw26Poster);
      }
      if (deferredSource && !deferredSource.hasAttribute('src')) {
        deferredSource.src = deferredSource.dataset.fw26Src;
        video.load();
      }
      if (!video.paused) {
        if (watchedVideo !== video) {
          watchProgress(video);
          updateControl('loading');
        }
        return;
      }
      const attempt = ++playAttempt;
      window.clearTimeout(playTimeout);
      video.muted = true;
      video.playsInline = true;
      updateControl('loading');
      watchProgress(video);
      const failed = (error) => {
        if (attempt !== playAttempt || video !== videos[activeVideoIndex] || !canRun()) return;
        window.clearTimeout(playTimeout);
        playTimeout = undefined;
        recoveryControl = true;
        root.dataset.fw26PlaybackError = error?.name || 'MediaError';
        updateControl('blocked');
      };
      try {
        const result = video.play();
        if (result?.then) result.then(() => {
          if (attempt !== playAttempt || video !== videos[activeVideoIndex] || !canRun()) return;
          // Resolution alone does not prove that decoded media is advancing.
        }).catch(failed);
      } catch (error) { failed(error); }
    };
    const syncPlayback = () => {
      if (!canRun()) {
        pauseMedia();
        updateControl(reducedMotion.matches ? 'reduced' : userPaused ? 'paused' : 'offscreen');
        return;
      }
      const video = videos[activeVideoIndex];
      if (video) {
        if (video.ended) show(activeIndex + 1);
        else startVideo(video);

      }
    };

    const show = (nextIndex) => {
      if (!videos.length) return;
      pauseMedia();
      retryPosition = 0;
      const slideCount = videos.length;
      activeIndex = (nextIndex + slideCount) % slideCount;
      activeVideoIndex = activeIndex;
      mobileImage?.classList.remove('is-active');
      videos.forEach((video, index) => {
        const active = index === activeVideoIndex;
        video.classList.toggle('is-active', active);
        if (active) {
          if (video.dataset.fw26Poster && !video.hasAttribute('poster')) {
            video.setAttribute('poster', video.dataset.fw26Poster);
          }
          video.currentTime = 0;
        } else {
          // Pausing alone can keep a previous clip downloading. Inactive hero
          // slides always restart at zero, so release only their media source.
          const source = video.querySelector('source[data-fw26-src]');
          if (source?.hasAttribute('src')) {
            source.removeAttribute('src');
            video.load();
          }
        }
      });
      progress.forEach((item, index) => item.classList.toggle('is-active', index === activeIndex));
      if (count) count.textContent = `${pad2(activeIndex + 1)} / ${pad2(slideCount)}`;
      syncPlayback();
    };

    videos.forEach((video, index) => {
      video.addEventListener('timeupdate', () => {
        if (index !== activeVideoIndex || !canRun() || video.paused || video.readyState < 2 || video.currentTime <= lastProgressTime) return;
        delete root.dataset.fw26PlaybackError;
        updateControl('playing');
        watchProgress(video);
      });
      video.addEventListener('loadedmetadata', () => {
        if (index === activeVideoIndex && retryPosition > 0) {
          video.currentTime = Math.min(retryPosition, video.duration || retryPosition);
          lastProgressTime = video.currentTime;
          retryPosition = 0;
        }
      });
      video.addEventListener('ended', () => {
        if (index === activeVideoIndex && canRun()) show(activeIndex + 1);
      });
      video.addEventListener('error', () => {
        if (index !== activeVideoIndex || !canRun()) return;
        recoveryControl = true;
        root.dataset.fw26PlaybackError = 'MediaError';
        updateControl('blocked');
      });
      video.addEventListener('pause', () => {
        if (index !== activeVideoIndex || video.ended || !video.paused || !canRun()) return;
        recoveryControl = true;
        updateControl('paused');
      });
    });
    playButton?.addEventListener('click', () => {
      const video = videos[activeVideoIndex];
      if (reducedMotion.matches) return;
      recoveryControl = true;
      if (video && (root.dataset.fw26Playback === 'stalled' || video.error)) {
        retryPosition = video.currentTime;
        pauseMedia();
        userPaused = false;
        video.load();
        syncPlayback();
        return;
      }
      userPaused = video ? !video.paused : !userPaused;
      if (!userPaused && video?.error) video.load();
      syncPlayback();
    });
    root.querySelector('[data-fw26-hero-prev]')?.addEventListener('click', () => show(activeIndex - 1));
    root.querySelector('[data-fw26-hero-next]')?.addEventListener('click', () => show(activeIndex + 1));
    const onResize = () => show(0);
    const onPolicyChange = () => syncPlayback();
    const onPageShow = (event) => { if (event.persisted) syncPlayback(); };
    mobileHero.addEventListener('change', onResize);
    reducedMotion.addEventListener('change', onPolicyChange);
    document.addEventListener('visibilitychange', onPolicyChange);
    window.addEventListener('pageshow', onPageShow);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      const visible = !!entries[0]?.isIntersecting && entries[0].intersectionRatio >= 0.1;
      if (visible !== heroVisible) {
        heroVisible = visible;
        syncPlayback();
      }
    }, { threshold: [0, 0.1] }) : null;
    observer?.observe(root);
    root.addEventListener('shopify:section:unload', () => {
      disposed = true;
      pauseMedia();
      observer?.disconnect();
      mobileHero.removeEventListener('change', onResize);
      reducedMotion.removeEventListener('change', onPolicyChange);
      document.removeEventListener('visibilitychange', onPolicyChange);
      window.removeEventListener('pageshow', onPageShow);
    }, { once: true });
    show(0);
  };

  const initRail = (root) => {
    if (!root || root.dataset.fw26Ready === 'true') return;
    root.dataset.fw26Ready = 'true';

    const viewport = root.querySelector('[data-fw26-viewport]');
    const track = root.querySelector('[data-fw26-track]');
    const pauseButton = root.querySelector('[data-fw26-pause]');
    const originals = [...track?.querySelectorAll('[data-fw26-card]') || []];
    if (!viewport || !track || !originals.length) return;

    const staticRail = root.hasAttribute('data-fw26-static');
    const mobileStaticRail = root.hasAttribute('data-fw26-static-mobile');
    const mobileRail = window.matchMedia('(max-width: 991px)');
    const motionBlocked = () => staticRail || (mobileStaticRail && mobileRail.matches);
    const clones = [];
    const appendCopy = () => originals.forEach((card) => {
      const clone = card.cloneNode(true);
      clone.removeAttribute('data-fw26-card');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.setAttribute('data-fw26-clone', '');
      clone.querySelectorAll('a, button').forEach((control) => control.setAttribute('tabindex', '-1'));
      track.appendChild(clone);
      clones.push(clone);
    });
    if (!staticRail) appendCopy();

    let paused = staticRail;
    let frame = 0;
    let lastTime = 0;
    let manualScrollUntil = 0;
    let scrollPosition = viewport.scrollLeft;
    let interacting = false;
    let focused = false;
    let disposed = false;
    let railVisible = root.getBoundingClientRect().bottom > 0 && root.getBoundingClientRect().top < window.innerHeight;
    let cachedLoopWidth = 0;
    const speed = Math.max(0, Number(root.dataset.fw26Speed) || 12);
    // The first repeated card is the exact period, including the seam gap.
    const getLoopWidth = () => clones[0]?.getBoundingClientRect().left - originals[0].getBoundingClientRect().left;
    const fillViewport = () => {
      if (motionBlocked()) return;
      const period = getLoopWidth();
      if (!(period > 0)) return;
      while (track.scrollWidth < period + viewport.clientWidth + 1) appendCopy();
      cachedLoopWidth = period;
    };
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(fillViewport) : null;
    resizeObserver?.observe(viewport);
    fillViewport();

    const updatePauseLabel = () => {
      if (!pauseButton) return;
      const stopped = paused || motionBlocked() || reducedMotion.matches;
      pauseButton.textContent = stopped ? 'Play' : 'Pause';
      pauseButton.setAttribute('aria-pressed', stopped ? 'true' : 'false');
      if (root.classList.contains('spclst-fw26-featured-rail')) pauseButton.setAttribute('aria-label', `${stopped ? 'Play' : 'Pause'} featured publication logos`);
      pauseButton.disabled = motionBlocked() || reducedMotion.matches;
      pauseButton.hidden = motionBlocked();
    };

    const tick = (time) => {
      frame = 0;
      if (!canAnimate()) return;
      if (!lastTime) lastTime = time;
      if (time < manualScrollUntil) {
        scrollPosition = viewport.scrollLeft;
      } else if (!paused && !interacting && !focused && !reducedMotion.matches && !motionBlocked() && !document.hidden) {
        if (Math.abs(viewport.scrollLeft - scrollPosition) > 2) scrollPosition = viewport.scrollLeft;
        scrollPosition += speed * Math.min(50, time - lastTime) / 1000;
        const loopWidth = cachedLoopWidth;
        if (loopWidth > 0 && scrollPosition >= loopWidth) scrollPosition %= loopWidth;
        viewport.scrollLeft = scrollPosition;
      }
      lastTime = time;
      frame = requestAnimationFrame(tick);
    };

    const canAnimate = () => !disposed && railVisible && !paused && !motionBlocked() && !reducedMotion.matches && !document.hidden;
    const syncAnimation = () => {
      if (canAnimate()) {
        if (!frame) {
          lastTime = 0;
          scrollPosition = viewport.scrollLeft;
          frame = requestAnimationFrame(tick);
        }
      } else {
        cancelAnimationFrame(frame);
        frame = 0;
        lastTime = 0;
      }
    };
    const railObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      railVisible = !!entries[0]?.isIntersecting;
      syncAnimation();
    }) : null;
    railObserver?.observe(root);
    document.addEventListener('visibilitychange', syncAnimation);
    const onMotionChange = () => { updatePauseLabel(); syncAnimation(); };

    pauseButton?.addEventListener('click', () => {
      paused = !paused;
      updatePauseLabel();
      syncAnimation();
    });
    const visibleCardWidth = () => originals.find((card) => card.getBoundingClientRect().width > 0)?.getBoundingClientRect().width || 0;
    const moveByCard = (direction) => {
      const width = visibleCardWidth();
      if (!width) return;
      manualScrollUntil = performance.now() + 800;
      viewport.scrollBy({ left: direction * (width + 5), behavior: motionBlocked() || reducedMotion.matches ? 'auto' : 'smooth' });
    };
    root.querySelector('[data-fw26-prev]')?.addEventListener('click', () => moveByCard(-1));
    root.querySelector('[data-fw26-next]')?.addEventListener('click', () => moveByCard(1));
    const beginInteraction = () => { interacting = true; scrollPosition = viewport.scrollLeft; };
    const endInteraction = () => { if (!interacting) return; interacting = false; manualScrollUntil = performance.now() + 1600; };
    const manualInteraction = () => { manualScrollUntil = performance.now() + 1600; scrollPosition = viewport.scrollLeft; };
    viewport.addEventListener('pointerdown', beginInteraction, { passive: true });
    window.addEventListener('pointerup', endInteraction, { passive: true });
    window.addEventListener('pointercancel', endInteraction, { passive: true });
    viewport.addEventListener('touchstart', beginInteraction, { passive: true });
    viewport.addEventListener('touchend', endInteraction, { passive: true });
    viewport.addEventListener('touchcancel', endInteraction, { passive: true });
    viewport.addEventListener('wheel', manualInteraction, { passive: true });
    viewport.addEventListener('focusin', () => { focused = true; });
    viewport.addEventListener('focusout', () => { focused = false; manualInteraction(); });
    const onBreakpoint = () => {
      fillViewport();
      if (motionBlocked()) viewport.scrollLeft = Math.min(viewport.scrollLeft, Math.max(0, viewport.scrollWidth - viewport.clientWidth));
      scrollPosition = viewport.scrollLeft;
      lastTime = 0;
      updatePauseLabel();
      syncAnimation();
    };
    mobileRail.addEventListener('change', onBreakpoint);
    reducedMotion.addEventListener('change', onMotionChange);
    root.addEventListener('shopify:section:unload', () => {
      disposed = true;
      cancelAnimationFrame(frame);
      railObserver?.disconnect();
      document.removeEventListener('visibilitychange', syncAnimation);
      resizeObserver?.disconnect();
      mobileRail.removeEventListener('change', onBreakpoint);
      reducedMotion.removeEventListener('change', onMotionChange);
      window.removeEventListener('pointerup', endInteraction);
      window.removeEventListener('pointercancel', endInteraction);
    }, { once: true });

    updatePauseLabel();
    syncAnimation();
  };

  const initStoryTransition = (root) => {
    if (!root || root.dataset.fw26StoryReady === 'true' || reducedMotion.matches) return;
    const image = root.querySelector('.spclst-fw26-mobile-story__image-wrap');
    if (!image) return;
    root.dataset.fw26StoryReady = 'true';
    const desktop = window.matchMedia('(min-width: 992px)');
    const clamp = (value) => Math.min(1, Math.max(0, value));
    let frame = 0;

    const update = () => {
      frame = 0;
      if (!desktop.matches) {
        root.classList.remove('is-motion-ready');
        return;
      }
      const top = image.getBoundingClientRect().top;
      const progress = clamp((window.innerHeight * 0.9 - top) / (window.innerHeight * 0.7));
      const expansion = clamp(progress / 0.62);
      const reveal = clamp((progress - 0.62) / 0.25);
      const darkness = clamp((progress - 0.2) / 0.7);
      root.style.setProperty('--fw26-story-inset', `${110 * (1 - expansion)}px`);
      root.style.setProperty('--fw26-story-darkness', String(darkness));
      root.style.setProperty('--fw26-story-reveal', String(reveal));
      root.style.setProperty('--fw26-story-lift', `${26 * (1 - reveal)}px`);
      root.classList.add('is-motion-ready');
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    desktop.addEventListener('change', schedule);
    root.addEventListener('shopify:section:unload', () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      desktop.removeEventListener('change', schedule);
      cancelAnimationFrame(frame);
    }, { once: true });
    update();
  };

  const initFieldLog = (root) => {
    if (!root || root.dataset.fw26Ready === 'true') return;
    root.dataset.fw26Ready = 'true';

    const slides = [...root.querySelectorAll('[data-fw26-field-slide]')];
    const tabs = [...root.querySelectorAll('[data-fw26-field-tab]')];
    const tabsViewport = root.querySelector('.spclst-fw26-field-log__tabs');
    const pauseButton = root.querySelector('[data-fw26-field-pause]');
    const muteButton = root.querySelector('[data-fw26-field-mute]');
    const youtubePlayers = new Map();
    const youtubePending = new WeakSet();
    const youtubeTiming = new WeakMap();
    const mobileWalkthrough = window.matchMedia('(max-width: 991px)');
    const nativePositions = new WeakMap();
    const nativeAttempts = new WeakMap();
    let activeIndex = 0;
    let paused = reducedMotion.matches;
    let muted = true;
    let visible = false;
    let disposed = false;
    let progressTimer = null;
    const inView = () => visible && !disposed && document.visibilityState !== 'hidden';
    const shouldRun = () => inView() && !paused;

    const stopNative = (video, release = false) => {
      nativeAttempts.set(video, (nativeAttempts.get(video) || 0) + 1);
      if (video.readyState > 0) nativePositions.set(video, video.currentTime);
      video.pause();
      if (release && video.hasAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
    };
    const syncNative = (video, active) => {
      video.muted = muted;
      if (!active || !inView()) {
        stopNative(video, true);
        return;
      }
      const variant = mobileWalkthrough.matches ? 'Mobile' : 'Desktop';
      const poster = video.dataset[`poster${variant}`];
      if (poster && video.getAttribute('poster') !== poster) video.setAttribute('poster', poster);
      const source = video.dataset[`src${variant}`];
      if (source && video.getAttribute('src') !== source) {
        stopNative(video, true);
        video.setAttribute('src', source);
        video.load();
      }
      if (!shouldRun()) {
        stopNative(video);
        return;
      }
      const attempt = (nativeAttempts.get(video) || 0) + 1;
      nativeAttempts.set(video, attempt);
      const playback = video.play();
      playback?.then(() => {
        if (nativeAttempts.get(video) === attempt && (!shouldRun() || !slides[activeIndex]?.contains(video))) video.pause();
      }).catch(() => {});
    };
    const updatePauseControl = () => {
      pauseButton?.setAttribute('aria-pressed', paused ? 'true' : 'false');
      pauseButton?.setAttribute('aria-label', paused ? 'Play product walkthrough' : 'Pause product walkthrough');
      const icon = pauseButton?.querySelector('img');
      if (icon) icon.src = paused ? pauseButton.dataset.playIcon : pauseButton.dataset.pauseIcon;
    };

    const sendYouTubeCommand = (frame, func, args = []) => {
      frame?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
    };

    const connectYouTubePlayer = (frame) => {
      if (!frame?.id || youtubePending.has(frame)) return;
      youtubePending.add(frame);
      loadYouTubeApi().then((YT) => {
        if (!YT?.Player || !frame.isConnected) return;
        const onStateChange = (event) => {
          if (event.data === 0 && shouldRun() && slides[activeIndex]?.contains(frame)) show(activeIndex + 1);
        };
        const onReady = (event) => {
          youtubePlayers.set(frame, event.target);
          if (muted) event.target.mute(); else event.target.unMute();
          if (slides[activeIndex]?.contains(frame) && shouldRun()) event.target.playVideo();
          else event.target.pauseVideo();
        };
        // Analytics may already have connected this iframe. Keep one player so
        // its event listeners and API methods remain attached to the same object.
        const existingPlayer = typeof YT.get === 'function' ? YT.get(frame.id) : null;
        if (existingPlayer) {
          existingPlayer.addEventListener('onReady', onReady);
          existingPlayer.addEventListener('onStateChange', onStateChange);
          if (typeof existingPlayer.getDuration === 'function') onReady({ target: existingPlayer });
        } else {
          new YT.Player(frame.id, { events: { onReady, onStateChange } });
        }
      });
    };

    const prepareYouTubeFrame = (frame, shouldPlay) => {
      if (!frame) return;

      const syncPlayback = () => {
        sendYouTubeCommand(frame, 'setOption', ['captions', 'track', {}]);
        sendYouTubeCommand(frame, muted ? 'mute' : 'unMute');
        const player = youtubePlayers.get(frame);
        if (player) {
          if (shouldPlay && shouldRun()) player.playVideo(); else player.pauseVideo();
        }
      };

      if (!frame.src && frame.dataset.src && inView() && shouldPlay) {
        frame.addEventListener('load', () => {
          syncPlayback();
          connectYouTubePlayer(frame);
        }, { once: true });
        frame.src = frame.dataset.src;
      } else {
        syncPlayback();
        connectYouTubePlayer(frame);
      }
    };

    const setMediaPlayback = (slide, shouldPlay) => {
      const video = slide?.querySelector('video');
      const frame = slide?.querySelector('[data-fw26-youtube]');
      if (video) {
        if (video.matches('[data-fw26-walkthrough-video]')) syncNative(video, shouldPlay);
        else {
          video.muted = muted;
          if (shouldPlay && shouldRun()) playSafely(video); else video.pause();
        }
      }
      if (frame && ((shouldPlay && inView()) || frame.src)) prepareYouTubeFrame(frame, shouldPlay && inView());
    };

    const activeMedia = () => ({
      frame: slides[activeIndex]?.querySelector('[data-fw26-youtube]'),
      video: slides[activeIndex]?.querySelector('video'),
    });

    const show = (nextIndex) => {
      if (!slides.length) return;
      activeIndex = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        const video = slide.querySelector('video');
        const frame = slide.querySelector('[data-fw26-youtube]');
        slide.classList.toggle('is-active', active);
        if (active && video) {
          nativePositions.set(video, 0);
          if (video.readyState > 0) video.currentTime = 0;
        }
        if (active && frame) {
          const previous = youtubeTiming.get(frame);
          youtubeTiming.set(frame, { currentTime: 0, duration: previous?.duration || 0 });
          if (frame.src) sendYouTubeCommand(frame, 'seekTo', [0, true]);
        }
        setMediaPlayback(slide, active);
      });
      tabs.forEach((tab, index) => {
        const active = index === activeIndex;
        tab.style.setProperty('--fw26-progress', '0');
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active && tabsViewport) {
          const tabBounds = tab.getBoundingClientRect();
          const viewportBounds = tabsViewport.getBoundingClientRect();
          tabsViewport.scrollBy({
            left: tabBounds.left - viewportBounds.left - (viewportBounds.width - tabBounds.width) / 2,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
          });
        }
      });
    };

    const updateProgress = () => {
      const media = activeMedia();
      const player = media.frame && youtubePlayers.get(media.frame);
      let currentTime = media.video?.currentTime || 0;
      let duration = media.video?.duration || 0;
      if (typeof player?.getCurrentTime === 'function' && typeof player?.getDuration === 'function') {
        currentTime = player.getCurrentTime() || 0;
        duration = player.getDuration() || 0;
      } else if (media.frame) {
        const timing = youtubeTiming.get(media.frame);
        currentTime = timing?.currentTime || 0;
        duration = timing?.duration || 0;
      }
      const progress = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;
      tabs[activeIndex]?.style.setProperty('--fw26-progress', String(progress));
    };

    slides.forEach((slide, index) => {
      const video = slide.querySelector('video');
      video?.addEventListener('loadedmetadata', () => {
        if (!video.hasAttribute('src')) return;
        const position = nativePositions.get(video) || 0;
        if (position > 0 && Number.isFinite(video.duration)) video.currentTime = Math.min(position, Math.max(0, video.duration - 0.1));
      });
      video?.addEventListener('ended', () => {
        if (index === activeIndex && shouldRun()) show(activeIndex + 1);
      });
    });
    tabs.forEach((tab) => tab.addEventListener('click', () => show(Number(tab.dataset.slideIndex))));
    root.querySelector('[data-fw26-field-prev]')?.addEventListener('click', () => show(activeIndex - 1));
    root.querySelector('[data-fw26-field-next]')?.addEventListener('click', () => show(activeIndex + 1));
    pauseButton?.addEventListener('click', () => {
      paused = !paused;
      updatePauseControl();
      syncPlayback();
    });
    muteButton?.addEventListener('click', () => {
      muted = !muted;
      slides.forEach((slide) => {
        const video = slide.querySelector('video');
        const frame = slide.querySelector('[data-fw26-youtube]');
        if (video) video.muted = muted;
        if (frame?.src) sendYouTubeCommand(frame, muted ? 'mute' : 'unMute');
      });
      muteButton.setAttribute('aria-pressed', muted ? 'true' : 'false');
      muteButton.setAttribute('aria-label', muted ? 'Unmute product walkthrough' : 'Mute product walkthrough');
      const muteIcon = muteButton.querySelector('img');
      if (muteIcon) muteIcon.src = muted ? muteButton.dataset.mutedIcon : muteButton.dataset.soundIcon;
    });

    const onYouTubeMessage = (event) => {
      let originHost = '';
      try { originHost = new URL(event.origin).hostname; } catch (_) { return; }
      if (!/youtube(?:-nocookie)?\.com$/.test(originHost)) return;
      let payload = event.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (_) { return; }
      }
      if (payload?.event === 'infoDelivery' && payload.info) {
        const frame = slides[activeIndex]?.querySelector('[data-fw26-youtube]');
        if (frame?.contentWindow === event.source) {
          const previous = youtubeTiming.get(frame) || { currentTime: 0, duration: 0 };
          youtubeTiming.set(frame, {
            currentTime: Number.isFinite(payload.info.currentTime) ? payload.info.currentTime : previous.currentTime,
            duration: Number.isFinite(payload.info.duration) ? payload.info.duration : previous.duration,
          });
        }
      }
      if (payload?.event !== 'onStateChange' || payload.info !== 0 || !shouldRun()) return;
      const activeFrame = slides[activeIndex]?.querySelector('[data-fw26-youtube]');
      if (activeFrame?.contentWindow === event.source) show(activeIndex + 1);
    };
    window.addEventListener('message', onYouTubeMessage);
    const syncProgressTimer = () => {
      const needsProgress = shouldRun();
      if (needsProgress && progressTimer === null) {
        updateProgress();
        progressTimer = window.setInterval(updateProgress, 100);
      } else if (!needsProgress && progressTimer !== null) {
        window.clearInterval(progressTimer);
        progressTimer = null;
      }
    };
    const syncPlayback = () => {
      slides.forEach((slide, index) => setMediaPlayback(slide, index === activeIndex));
      syncProgressTimer();
    };
    const observer = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.1);
      syncPlayback();
    }, { threshold: [0, 0.1] });
    observer.observe(root);
    const onMotionChange = () => {
      if (reducedMotion.matches) paused = true;
      updatePauseControl();
      syncPlayback();
    };
    mobileWalkthrough.addEventListener('change', syncPlayback);
    reducedMotion.addEventListener('change', onMotionChange);
    document.addEventListener('visibilitychange', syncPlayback);
    root.addEventListener('shopify:section:unload', () => {
      disposed = true;
      observer.disconnect();
      mobileWalkthrough.removeEventListener('change', syncPlayback);
      reducedMotion.removeEventListener('change', onMotionChange);
      document.removeEventListener('visibilitychange', syncPlayback);
      syncPlayback();
      window.removeEventListener('message', onYouTubeMessage);
      window.clearInterval(progressTimer);
    }, { once: true });

    updatePauseControl();
    show(0);
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-fw26-hero]').forEach(initHero);
    scope.querySelectorAll('[data-fw26-rail]').forEach(initRail);
    scope.querySelectorAll('.spclst-fw26-mobile-story__brand').forEach(initStoryTransition);
    scope.querySelectorAll('[data-fw26-field-log]').forEach(initFieldLog);
  };

  window.SPCLSTFW26Homepage = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
